"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import Icon from "@/components/ui/AppIcon";
import { useCart } from "./CartContext";
import type { VoucherData, DiscountVoucherData } from "./CartContext";

import { createClient } from "@/lib/supabase/client";
import { submitPayFastForm } from "@/lib/payfast-form";
import CartStepCart from "./CartStepCart";
import CartStepDetails from "./CartStepDetails";
import CartStepPayment from "./CartStepPayment";
import CartStepSuccess from "./CartStepSuccess";
import VoucherErrorModal from "@/components/ui/VoucherErrorModal";
import DespatchModal from "./DespatchModal";
import DeliveryCalculator from "./DeliveryCalculator";

type CheckoutStep = "cart" | "details" | "payment" | "eft-success" | "confirmation";
type PaymentMethod = "eft" | "voucher" | "payfast";
type DespatchPhase = "idle" | "choosing" | "calculating";
type DespatchMethod = "collection" | "delivery";

const INITIAL_FORM = { name: "", email: "", phone: "", date: "", address: "", notes: "" };

export default function CartSidebar() {
  const { items, subtotal, totalItems, isOpen, setIsOpen, clearCart } = useCart();
  const supabase = createClient();

  const [step, setStep] = useState<CheckoutStep>("cart");
  const [form, setForm] = useState(INITIAL_FORM);
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod>("payfast");
  const [processing, setProcessing] = useState(false);
  const [payError, setPayErrorState] = useState("");
  const [phoneError, setPhoneErrorState] = useState("");
  const [orderRef, setOrderRef] = useState("");
  const [savedOrderTotal, setSavedOrderTotal] = useState(0);
  const voucherOrderInProgress = useRef(false);

  // Despatch state
  const [despatchPhase, setDespatchPhase] = useState<DespatchPhase>("idle");
  const [despatchMethod, setDespatchMethod] = useState<DespatchMethod | null>(null);
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [despatchConfirmed, setDespatchConfirmed] = useState(false);

  // Global error modal
  const [errorModal, setErrorModal] = useState<{ open: boolean; message: string; title: string }>({ open: false, message: "", title: "Error" });
  const showError = (message: string, title = "Error") => setErrorModal({ open: true, message, title });
  const closeError = () => setErrorModal({ open: false, message: "", title: "Error" });

  // Wrapped setters that show modal instead of inline
  const setPayError = (msg: string) => { if (msg) showError(msg, "Payment Error"); else setPayErrorState(""); };
  const setPhoneError = (msg: string) => { if (msg) showError(msg, "Phone Number Error"); else setPhoneErrorState(""); };
  const setVoucherError = (msg: string) => { if (msg) showError(msg, "Voucher Error"); };
  const setDvError = (msg: string) => { if (msg) showError(msg, "Discount Voucher Error"); };

  // Meal Voucher state
  const [voucherCode, setVoucherCode] = useState("");
  const [voucherData, setVoucherData] = useState<VoucherData | null>(null);
  const [voucherLoading, setVoucherLoading] = useState(false);
  const [voucherApplied, setVoucherApplied] = useState(false);
  const [showVoucherSection, setShowVoucherSection] = useState(false);
  const [voucherMealsUsed, setVoucherMealsUsed] = useState(0);
  const [voucherMealsRemaining, setVoucherMealsRemaining] = useState(0);

  // Discount Voucher state
  const [dvCode, setDvCode] = useState("");
  const [dvData, setDvData] = useState<DiscountVoucherData | null>(null);
  const [dvLoading, setDvLoading] = useState(false);
  const [dvApplied, setDvApplied] = useState(false);
  const [showDvSection, setShowDvSection] = useState(false);

  const tax = subtotal * 0;
  const delivery = subtotal > 0 ? deliveryFee : 0;
  const total = subtotal + tax + delivery;
  const discountedTotal = dvApplied && dvData ? Math.max(0, total - dvData.dv_amount) : total;

  const getDetailsForm = useCallback(() => {
    if (voucherApplied && voucherData) {
      return {
        ...INITIAL_FORM,
        name: voucherData.customer_name || "",
        email: voucherData.customer_email || "",
        phone: voucherData.customer_phone || "",
      };
    }
    return { ...INITIAL_FORM };
  }, [voucherApplied, voucherData]);

  const resetCheckoutState = useCallback(() => {
    setStep("cart");
    setForm({ ...INITIAL_FORM });
    setOrderRef("");
    setSavedOrderTotal(0);
    setPayErrorState("");
    setPhoneErrorState("");
    setProcessing(false);
    setSelectedMethod("payfast");
    setDespatchPhase("idle");
    setDespatchMethod(null);
    setDeliveryFee(0);
    setDespatchConfirmed(false);
  }, []);

  const prevIsOpen = useRef(isOpen);
  useEffect(() => {
    if (isOpen && !prevIsOpen.current) {
      resetCheckoutState();
    }
    prevIsOpen.current = isOpen;
  }, [isOpen, resetCheckoutState]);

  // Despatch handlers
  const handleEnterDetails = () => {
    setDespatchPhase("choosing");
  };

  const handleDespatchChoice = (choice: DespatchMethod) => {
    setDespatchMethod(choice);
    setDespatchPhase("calculating");
  };

  const handleDespatchConfirm = (result: { deliveryCost: number; total: number; distanceKm?: number; distanceText?: string; durationText?: string; deliveryAddress?: string }) => {
    setDeliveryFee(result.deliveryCost);
    setDespatchConfirmed(true);
    setDespatchPhase("idle");
    // Now proceed to details step
    const baseForm = getDetailsForm();
    setForm({
      ...baseForm,
      address: despatchMethod === "delivery" ? (result.deliveryAddress || "") : "",
    });
    setPhoneErrorState("");
    setPayErrorState("");
    setStep("details");
  };

  const handleDespatchCancel = () => {
    setDespatchMethod(null);
    setDespatchPhase("choosing");
    setDespatchConfirmed(false);
  };

  const handleDespatchBackToIdle = () => {
    setDespatchPhase("idle");
    setDespatchMethod(null);
    setDespatchConfirmed(false);
  };

  const handleDetailsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const stripped = form.phone.replace(/\D/g, "");
    if (stripped.length !== 10) { setPhoneError("Mobile number must be exactly 10 digits"); return; }
    if (stripped[0] !== "0") { setPhoneError("Mobile number must start with 0 (e.g. 0821234567)"); return; }
    const ref = `CK-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2, 5).toUpperCase()}`;
    setOrderRef(ref);
    setStep("payment");
  };

  const handleVoucherDetailsConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    const stripped = form.phone.replace(/\D/g, "");
    if (stripped.length !== 10) { setPhoneError("Mobile number must be exactly 10 digits"); return; }
    if (stripped[0] !== "0") { setPhoneError("Mobile number must start with 0 (e.g. 0821234567)"); return; }
    const ref = `CK-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2, 5).toUpperCase()}`;
    setOrderRef(ref);
    await handleVoucherOrder(ref);
  };

  const handleVoucherOrder = async (overrideRef?: string) => {
    if (!voucherData) return;
    if (voucherOrderInProgress.current) return;
    voucherOrderInProgress.current = true;
    setProcessing(true);

    const mealsToDeduct = totalItems;
    if (mealsToDeduct > voucherData.meals_remaining) {
      setPayError(`Your voucher only has ${voucherData.meals_remaining} meal(s) remaining, but your cart has ${mealsToDeduct} item(s).`);
      setProcessing(false);
      voucherOrderInProgress.current = false;
      return;
    }

    try {
      const ref = overrideRef ?? orderRef;
      const response = await fetch("/api/orders/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          m_payment_id: ref,
          customer_name: form.name,
          customer_email: form.email,
          customer_phone: form.phone,
          items: items.map((i) => ({
            id: i.product.id, name: i.product.name, quantity: i.quantity,
            price: i.product.price, unit: i.product.unit, category: i.product.category,
            package_type: i.product.packageType || 'none',
          })),
          subtotal, delivery_fee: delivery, total: 0,
          payment_status: "paid", payment_method: "voucher",
          event_date: form.date || null, delivery_address: form.address,
          notes: `Voucher: ${voucherData.voucher_code}. ${form.notes}`.trim(),
        }),
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Failed to place order. Please try again.");

      const newRef = result.reference ?? ref;
      const newRemaining = voucherData.meals_remaining - mealsToDeduct;
      const newStatus = newRemaining <= 0 ? "redeemed" : "active";

      await supabase.from("vouchers").update({ meals_remaining: newRemaining, status: newStatus }).eq("voucher_code", voucherData.voucher_code);
      await supabase.from("voucher_redemptions").insert({
        voucher_code: voucherData.voucher_code,
        order_id: newRef,
        meals_used: mealsToDeduct,
        customer_name: form.name || voucherData.customer_name,
        customer_email: form.email || voucherData.customer_email,
        meals_remaining_before: voucherData.meals_remaining,
        meals_remaining_after: newRemaining,
        products_ordered: items.map((i) => ({
          id: i.product.id, name: i.product.name, quantity: i.quantity,
          category: i.product.category, price: i.product.price,
        })),
        notes: `Order ${newRef} — ${mealsToDeduct} meal(s) redeemed`,
      });

      setVoucherMealsUsed(mealsToDeduct);
      setVoucherMealsRemaining(newRemaining);
      setOrderRef(newRef);
      clearCart();
      setStep("eft-success");
    } catch (err) {
      setPayError(err instanceof Error ? err.message : "Failed to place order. Please try again.");
    } finally {
      setProcessing(false);
      voucherOrderInProgress.current = false;
    }
  };

  const handleEFTConfirm = async () => {
    setProcessing(true);
    // Capture the total immediately before any async operations or state changes
    const capturedTotal = discountedTotal;
    try {
      const orderNotes = dvApplied && dvData
        ? `Discount Voucher: ${dvData.dv_code} (R${dvData.dv_amount.toFixed(2)} credit). ${form.notes}`.trim()
        : form.notes;

      const response = await fetch("/api/orders/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          m_payment_id: orderRef,
          customer_name: form.name, customer_email: form.email, customer_phone: form.phone,
          items: items.map((i) => ({
            id: i.product.id, name: i.product.name, quantity: i.quantity,
            price: i.product.price, unit: i.product.unit, category: i.product.category,
            package_type: i.product.packageType || 'none',
          })),
          subtotal, delivery_fee: delivery, total: capturedTotal,
          payment_status: "awaiting_payment", payment_method: "eft",
          event_date: form.date || null, delivery_address: form.address, notes: orderNotes,
        }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Failed to place order. Please try again.");

      if (dvApplied && dvData) {
        await supabase
          .from("discount_vouchers")
          .update({ times_used: dvData.times_used + 1 })
          .eq("dv_code", dvData.dv_code);
      }

      setSavedOrderTotal(capturedTotal);
      setOrderRef(result.reference ?? orderRef);
      clearCart();
      setStep("eft-success");
    } catch (err) {
      setPayError(err instanceof Error ? err.message : "Failed to place order. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  const handlePayFastCheckout = async () => {
    setProcessing(true);
    try {
      const orderNotes = dvApplied && dvData
        ? `Discount Voucher: ${dvData.dv_code} (R${dvData.dv_amount.toFixed(2)} credit). ${form.notes}`.trim()
        : form.notes;

      // Step 1: Get signed PayFast payload from server
      const nameParts = form.name.trim().split(" ");
      const firstName = nameParts[0] || form.name;
      const lastName = nameParts.slice(1).join(" ") || "-";

      const initiateRes = await fetch("/api/payfast/initiate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          order: {
            paymentId: orderRef,
            amount: discountedTotal,
            itemName: `Cardamom Kitchen Order ${orderRef}`,
            itemDescription: items.map((i) => `${i.product.name} x${i.quantity}`).join(", "),
          },
          buyer: {
            firstName,
            lastName,
            email: form.email,
            cell: form.phone,
          },
        }),
      });

      const initiateResult = await initiateRes.json();
      if (!initiateRes.ok) throw new Error(initiateResult.error || "Failed to initiate PayFast payment.");

      // Step 2: Store the full order payload as a pending payment record.
      // The actual order row will only be created by the ITN handler once
      // PayFast confirms COMPLETE — no DB record is created here.
      const pendingRes = await fetch("/api/payfast/pending", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          m_payment_id: orderRef,
          payment_type: "order",
          payload: {
            m_payment_id: orderRef,
            customer_name: form.name,
            customer_email: form.email,
            customer_phone: form.phone,
            items: items.map((i) => ({
              id: i.product.id, name: i.product.name, quantity: i.quantity,
              price: i.product.price, unit: i.product.unit, category: i.product.category,
              package_type: i.product.packageType || 'none',
            })),
            subtotal,
            delivery_fee: delivery,
            total: discountedTotal,
            event_date: form.date || null,
            delivery_address: form.address,
            notes: orderNotes,
          },
        }),
      });

      if (!pendingRes.ok) {
        const pendingErr = await pendingRes.json();
        throw new Error(pendingErr.error || "Failed to prepare payment.");
      }

      // Step 3: Apply discount voucher usage if applicable
      if (dvApplied && dvData) {
        await supabase
          .from("discount_vouchers")
          .update({ times_used: dvData.times_used + 1 })
          .eq("dv_code", dvData.dv_code);
      }

      // Step 4: Build and auto-submit form to PayFast gateway
      try {
        sessionStorage.setItem("order_pending_m_payment_id", orderRef);
      } catch {
        // Non-blocking
      }
      clearCart();

      submitPayFastForm(initiateResult.gatewayUrl, initiateResult.fields);
    } catch (err) {
      setPayError(err instanceof Error ? err.message : "Failed to initiate PayFast payment.");
      setProcessing(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    resetCheckoutState();
  };

  if (!isOpen) return null;

  return (
    <>
      <VoucherErrorModal
        isOpen={errorModal.open}
        message={errorModal.message}
        title={errorModal.title}
        onClose={closeError}
      />
      <div className="fixed inset-0 bg-[#1A1612]/40 backdrop-blur-sm z-40" onClick={handleClose} aria-hidden="true" />
      <aside className="cart-panel fixed right-0 top-0 h-full w-full max-w-md bg-[#F5F0E8] z-50 shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#DDD5C8]">
          <div className="flex items-center gap-3">
            {step !== "cart" && step !== "confirmation" && step !== "eft-success" && (
              <button
                onClick={() => setStep(step === "payment" ? "details" : "cart")}
                className="p-1.5 rounded-lg hover:bg-[#EDE7DA] transition-colors mr-1"
                aria-label="Go back"
              >
                <Icon name="ArrowLeftIcon" size={16} className="text-[#5C5347]" />
              </button>
            )}
            {step === "cart" && despatchPhase !== "idle" && (
              <button
                onClick={handleDespatchBackToIdle}
                className="p-1.5 rounded-lg hover:bg-[#EDE7DA] transition-colors mr-1"
                aria-label="Go back"
              >
                <Icon name="ArrowLeftIcon" size={16} className="text-[#5C5347]" />
              </button>
            )}
            <h2 className="font-display text-lg font-semibold text-[#1A1612]">
              {step === "cart" && despatchPhase === "idle" && `Order Summary (${totalItems})`}
              {step === "cart" && despatchPhase === "choosing" && "Despatch Method"}
              {step === "cart" && despatchPhase === "calculating" && (despatchMethod === "collection" ? "Collection" : "Delivery")}
              {step === "details" && "Your Details"}
              {step === "payment" && "Secure Payment"}
              {step === "eft-success" && "Order Placed!"}
              {step === "confirmation" && "Order Confirmed!"}
            </h2>
          </div>
          <button onClick={handleClose} className="p-2 rounded-full hover:bg-[#EDE7DA] transition-colors" aria-label="Close cart">
            <Icon name="XMarkIcon" size={18} className="text-[#5C5347]" />
          </button>
        </div>

        {/* Progress Steps */}
        {step !== "confirmation" && step !== "eft-success" && despatchPhase === "idle" && (
          <div className="px-6 py-3 border-b border-[#DDD5C8] flex items-center gap-2">
            {(["cart", "details", "payment"] as CheckoutStep[]).map((s, i) => (
              <div key={s} className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                  s === step
                    ? "bg-[#C4622D] text-white"
                    : (["cart", "details", "payment"].indexOf(step) > i)
                    ? "bg-[#1A1612] text-white" : "bg-[#EDE7DA] text-[#8C8278]"
                  }`}
                >
                  {(["cart", "details", "payment"].indexOf(step) > i) ? (
                    <Icon name="CheckIcon" size={12} />
                  ) : i + 1}
                </div>
                <span className={`text-xs font-medium capitalize ${s === step ? "text-[#C4622D]" : "text-[#B5ADA5]"}`}>
                  {s === "cart" ? "Cart" : s === "details" ? "Details" : "Payment"}
                </span>
                {i < 2 && <div className="w-4 h-px bg-[#DDD5C8]" />}
              </div>
            ))}
          </div>
        )}

        {/* Steps */}
        {step === "cart" && despatchPhase === "idle" && (
          <CartStepCart
            voucherCode={voucherCode}
            setVoucherCode={setVoucherCode}
            voucherData={voucherData}
            setVoucherData={setVoucherData}
            voucherError=""
            setVoucherError={setVoucherError}
            voucherLoading={voucherLoading}
            setVoucherLoading={setVoucherLoading}
            voucherApplied={voucherApplied}
            setVoucherApplied={setVoucherApplied}
            showVoucherSection={showVoucherSection}
            setShowVoucherSection={setShowVoucherSection}
            dvCode={dvCode}
            setDvCode={setDvCode}
            dvData={dvData}
            setDvData={setDvData}
            dvError=""
            setDvError={setDvError}
            dvLoading={dvLoading}
            setDvLoading={setDvLoading}
            dvApplied={dvApplied}
            setDvApplied={setDvApplied}
            showDvSection={showDvSection}
            setShowDvSection={setShowDvSection}
            onProceed={handleEnterDetails}
            subtotal={subtotal}
            tax={tax}
            delivery={delivery}
            total={total}
          />
        )}

        {step === "cart" && despatchPhase === "choosing" && (
          <DespatchModal onChoice={handleDespatchChoice} />
        )}

        {step === "cart" && despatchPhase === "calculating" && despatchMethod && (
          <DeliveryCalculator
            despatchMethod={despatchMethod}
            orderSubtotal={subtotal}
            minimumFee={0}
            onConfirm={handleDespatchConfirm}
            onCancel={handleDespatchCancel}
          />
        )}

        {step === "details" && (
          <CartStepDetails
            form={form}
            setForm={setForm}
            phoneError=""
            setPhoneError={setPhoneError}
            payError=""
            voucherApplied={voucherApplied}
            voucherData={voucherData}
            processing={processing}
            totalItems={totalItems}
            despatchMethod={despatchMethod}
            onSubmit={voucherApplied ? handleVoucherDetailsConfirm : handleDetailsSubmit}
          />
        )}

        {step === "payment" && (
          <CartStepPayment
            voucherApplied={voucherApplied}
            voucherData={voucherData}
            dvApplied={dvApplied}
            dvData={dvData}
            selectedMethod={selectedMethod}
            setSelectedMethod={setSelectedMethod}
            orderRef={orderRef}
            subtotal={subtotal}
            delivery={delivery}
            total={total}
            discountedTotal={discountedTotal}
            totalItems={totalItems}
            payError=""
            processing={processing}
            onEFTConfirm={handleEFTConfirm}
            onVoucherOrder={() => handleVoucherOrder()}
            onPayFastCheckout={handlePayFastCheckout}
            buyerFirstName={form.name.trim().split(" ")[0] || form.name}
            buyerLastName={form.name.trim().split(" ").slice(1).join(" ") || "-"}
            buyerEmail={form.email}
            buyerCell={form.phone}
            cartItems={items.map((i) => ({ name: i.product.name, quantity: i.quantity, price: i.product.price }))}
          />
        )}

        {step === "eft-success" && (
          <CartStepSuccess
            orderRef={orderRef}
            orderTotal={savedOrderTotal}
            form={form}
            voucherApplied={voucherApplied}
            voucherData={voucherData}
            voucherMealsUsed={voucherMealsUsed}
            voucherMealsRemaining={voucherMealsRemaining}
            onClose={handleClose}
          />
        )}

        {step === "confirmation" && (
          <div className="flex-1 flex flex-col items-center justify-center px-6 py-10 text-center gap-6">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
              <Icon name="CheckIcon" size={36} className="text-green-600" />
            </div>
            <div>
              <h3 className="font-display text-2xl font-semibold text-[#1A1612] mb-2">Booking Confirmed!</h3>
              <p className="text-[#8C8278] text-sm leading-relaxed">
                Thank you, {form.name || "valued customer"}! We&apos;ve received your order and will send a confirmation to{" "}
                <span className="text-[#C4622D] font-medium">{form.email || "your email"}</span> within 2 hours.
              </p>
            </div>
            <div className="w-full bg-[#EDE7DA] rounded-2xl p-5 text-left space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-[#8C8278]">Event Date</span>
                <span className="font-semibold text-[#1A1612]">{form.date || "TBD"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#8C8278]">Payment</span>
                <span className="font-semibold text-green-600">✓ Processed</span>
              </div>
            </div>
            <button onClick={handleClose} className="w-full bg-[#C4622D] text-white py-3.5 rounded-full font-semibold text-sm hover:bg-[#A04E22] transition-all">
              Back to Menu
            </button>
          </div>
        )}
      </aside>
    </>
  );
}